
import React, { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/context/AuthContext";
import OrderDetails, { parseVoNumbers } from "./form/OrderDetails";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import StatusSelect from "./form/StatusSelect";
import PriorityCheckbox from "./form/PriorityCheckbox";
import NotificationRecipientSelect from "./NotificationRecipientSelect";
import FormActions from "./form/FormActions";
import InkoopProductReview from "./form/InkoopProductReview";
import { SelectedOrderProductData } from "@/types/notifications";
import { OrderProduct } from "@/hooks/useOrderProducts";
import { useBulkOrderProducts, BulkOrderEntry } from "@/hooks/useBulkOrderProducts";
import { toast } from "sonner";

interface CreateNotificationFormProps {
  notification: {
    type: string;
    voNumber: string;
    exactOrderNumber: string;
    message: string;
    priority: string | null;
    userId: string;
    userName: string;
    status: string;
    recipients: {
      internal: {
        group1: boolean;
        group2: boolean;
      };
      external: string[];
    };
    selectedOrderProducts?: SelectedOrderProductData[];
    isOverarchingNotification?: boolean;
  };
  isPending: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onSelectChange: (name: string, value: string) => void;
  onCheckboxChange: (name: string, value: string | boolean) => void;
  onInternalCheckboxChange: (group: 'group1' | 'group2') => void;
  onToggleExternalRecipient: (recipient: string) => void;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent, bulkEntries?: BulkOrderEntry[]) => void;
  onToggleOrderProduct: (product: OrderProduct) => void;
  onOrderProductMessageChange: (sku: string, message: string) => void;
  onOverarchingChange: (value: boolean) => void;
}

const CreateNotificationForm = ({
  notification,
  isPending,
  onInputChange,
  onSelectChange,
  onCheckboxChange,
  onInternalCheckboxChange,
  onToggleExternalRecipient,
  onCancel,
  onSubmit,
  onToggleOrderProduct,
  onOrderProductMessageChange,
  onOverarchingChange,
}: CreateNotificationFormProps) => {
  const { user } = useAuth();
  const isDecoprof = user?.company === "decoprof";
  const isMobile = useIsMobile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInkoopReview, setShowInkoopReview] = useState(false);

  // Bulk state
  const { entries: bulkEntries, fetchProgress, isFetching: isBulkFetching, fetchAll, updateEntry, updateAllEntries, removeEntry, clearEntries } = useBulkOrderProducts();

  const parsedVoNumbers = parseVoNumbers(notification.voNumber);
  const isBulk = parsedVoNumbers.length > 1;
  const hasBulkFetched = bulkEntries.length > 0 && !isBulkFetching;
  
  const hasVoNumber = notification.voNumber.trim() !== '';
  const hasProductMessages = notification.selectedOrderProducts && 
    notification.selectedOrderProducts.length > 0 &&
    notification.selectedOrderProducts.some(p => p.productMessage?.trim());
  const hasFreeMessage = notification.message.trim() !== '';
  
  // For bulk: valid if bulk entries fetched and at least some have products
  const bulkValid = isBulk && hasBulkFetched && bulkEntries.some(e => !e.error && e.products.length > 0);
  
  const isFormComplete = 
    notification.type !== '' && 
    notification.status !== '' &&
    (isBulk ? bulkValid : (hasVoNumber ? (hasProductMessages || hasFreeMessage) : hasFreeMessage));
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (isMobile) {
      document.body.classList.add('form-submitting');
    }
    
    // Pass bulk entries if in bulk mode
    if (isBulk && hasBulkFetched) {
      onSubmit(e, bulkEntries);
    } else {
      onSubmit(e);
    }
  };

  const handleInkoopCheckboxChange = (group: 'group1' | 'group2') => {
    if (group === 'group2') {
      const willBeChecked = !notification.recipients.internal.group2;
      
      if (willBeChecked && notification.selectedOrderProducts && notification.selectedOrderProducts.length > 0) {
        setShowInkoopReview(true);
        return;
      }
      
      if (willBeChecked && (!notification.selectedOrderProducts || notification.selectedOrderProducts.length === 0)) {
        onInternalCheckboxChange(group);
        return;
      }
    }
    
    onInternalCheckboxChange(group);
  };

  const handleInkoopConfirm = (updatedProducts: SelectedOrderProductData[]) => {
    const currentProducts = notification.selectedOrderProducts || [];
    
    currentProducts.forEach(p => {
      if (!updatedProducts.find(up => up.sku === p.sku)) {
        onToggleOrderProduct({
          sku: p.sku,
          ean: p.ean,
          productTitle: p.productTitle,
          variantTitle: p.variantTitle,
          quantity: p.quantity,
          priceIncl: 0,
          priceExcl: 0,
          imageUrl: p.imageUrl,
          brandTitle: p.brandTitle,
          artikelCode: p.artikelCode,
          color: p.color,
          productId: p.productId,
        } as OrderProduct);
      }
    });

    updatedProducts.forEach(up => {
      const original = currentProducts.find(p => p.sku === up.sku);
      if (original) {
        if (original.quantity !== up.quantity || original.color !== up.color) {
          const currentMsg = original.productMessage || '';
          const quantityNote = original.quantity !== up.quantity ? `[Aantal: ${up.quantity}]` : '';
          const colorNote = original.color !== up.color ? `[Kleur: ${up.color || 'geen'}]` : '';
          const notes = [quantityNote, colorNote].filter(Boolean).join(' ');
          onOrderProductMessageChange(up.sku, currentMsg ? `${currentMsg} ${notes}` : notes);
        }
      }
    });
    
    onInternalCheckboxChange('group2');
    setShowInkoopReview(false);
    toast.success(`Inkoop bevestigd voor ${updatedProducts.length} product(en)`);
  };

  const handleInkoopCancel = () => {
    setShowInkoopReview(false);
  };

  return (
    <>
      <form 
        onSubmit={handleSubmit} 
        className="space-y-4" 
        data-submitting={isSubmitting ? "true" : "false"}
        style={isMobile ? {
          position: 'static',
          transform: 'none',
          touchAction: 'auto',
          pointerEvents: 'auto'
        } : undefined}
      >
        <OrderDetails
          type={notification.type}
          voNumber={notification.voNumber}
          exactOrderNumber={notification.exactOrderNumber}
          onTypeChange={(value) => onSelectChange('type', value)}
          onInputChange={onInputChange}
          selectedOrderProducts={notification.selectedOrderProducts?.map(p => ({ ...p })) || []}
          isOverarchingNotification={notification.isOverarchingNotification || false}
          onToggleOrderProduct={onToggleOrderProduct}
          onOrderProductMessageChange={onOrderProductMessageChange}
          onOverarchingChange={onOverarchingChange}
          onStatusChange={(value) => onSelectChange('status', value)}
          onMessageChange={(msg) => onInputChange({ target: { name: 'message', value: msg } } as React.ChangeEvent<HTMLInputElement>)}
          bulkEntries={bulkEntries}
          isBulkFetching={isBulkFetching}
          bulkFetchProgress={fetchProgress}
          onBulkFetch={fetchAll}
          onBulkUpdateEntry={updateEntry}
          onBulkUpdateAll={updateAllEntries}
          onBulkRemoveEntry={removeEntry}
          onBulkClear={clearEntries}
        />

        {!isBulk && !hasVoNumber && (
          <div className="space-y-2">
            <Label htmlFor="message" className="flex">
              Bericht
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Textarea
              id="message"
              name="message"
              placeholder="Beschrijf de melding..."
              value={notification.message}
              onChange={onInputChange}
              required
            />
          </div>
        )}
        
        {!isDecoprof && (
          <NotificationRecipientSelect
            internalRecipients={notification.recipients.internal}
            onInternalCheckboxChange={handleInkoopCheckboxChange}
          />
        )}
        
        <StatusSelect 
          status={notification.status} 
          onStatusChange={(value) => onSelectChange('status', value)} 
        />
        
        <PriorityCheckbox 
          isPriorityHigh={notification.priority === 'hoog'}
          onPriorityChange={(checked) => {
            onCheckboxChange('priority', checked === true ? 'hoog' : 'normaal');
          }}
        />
        
        <FormActions
          isPending={isPending}
          isFormComplete={isFormComplete}
          isMobile={isMobile}
          onCancel={onCancel}
        />
      </form>

      <InkoopProductReview
        open={showInkoopReview}
        onOpenChange={setShowInkoopReview}
        products={notification.selectedOrderProducts || []}
        onConfirm={handleInkoopConfirm}
        onCancel={handleInkoopCancel}
      />
    </>
  );
};

export default CreateNotificationForm;
